import { useEffect, useState, useCallback } from 'react';
import { Download, X, Share, Bell, RefreshCw, Wifi } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type Platform = 'ios' | 'android' | 'desktop' | 'unknown';

function detectPlatform(): Platform {
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  if (/Windows|Macintosh|Linux/.test(ua) && !/Android/.test(ua)) return 'desktop';
  return 'unknown';
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as any).standalone === true
  );
}

const STORAGE_KEY = 'nudge:install-prompt-hidden';

export function InstallPrompt() {
  const [show, setShow] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [platform] = useState<Platform>(detectPlatform);

  useEffect(() => {
    // Don't show on desktop, if already installed, or if user opted out
    if (platform === 'desktop' || platform === 'unknown') return;
    if (isStandalone()) return;
    if (localStorage.getItem(STORAGE_KEY) === 'true') return;

    // Show after a short delay
    const timer = setTimeout(() => setShow(true), 2500);
    return () => clearTimeout(timer);
  }, [platform]);

  // Capture Android install prompt
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleDismiss = useCallback(() => {
    if (dontShowAgain) {
      localStorage.setItem(STORAGE_KEY, 'true');
    }
    setShow(false);
  }, [dontShowAgain]);

  const handleAndroidInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShow(false);
    }
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 p-6 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            className="w-full max-w-sm rounded-[28px] bg-white px-6 py-6 shadow-2xl"
          >
            {/* Close button */}
            <button
              onClick={handleDismiss}
              className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-400"
            >
              <X size={16} />
            </button>

            {/* App icon */}
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-white shadow-lg border border-slate-100">
              <svg viewBox="0 0 512 512" className="h-7 w-7">
                <path
                  d="M 0 -30 C -50 -80 -150 -50 -150 30 C -150 110 0 200 0 200 C 0 200 150 110 150 30 C 150 -50 50 -80 0 -30 Z"
                  fill="#fa8585"
                  transform="translate(256, 200) scale(1.3)"
                />
              </svg>
            </div>

            {/* Title */}
            <h2 className="mb-1 text-center text-lg font-bold text-slate-900">Install Nudge</h2>
            <p className="mb-4 text-center text-[13px] text-slate-500">
              Add to your home screen for the full experience.
            </p>

            {/* Benefits — compact 2x2 grid */}
            <div className="mb-5 grid grid-cols-2 gap-2">
              <BenefitChip icon={<Bell size={13} />} text="Notifications" />
              <BenefitChip icon={<RefreshCw size={13} />} text="Auto-updates" />
              <BenefitChip icon={<Wifi size={13} />} text="Works offline" />
              <BenefitChip icon={<Download size={13} />} text="Full-screen" />
            </div>

            {/* Platform-specific instructions */}
            {platform === 'android' && deferredPrompt ? (
              <button
                onClick={handleAndroidInstall}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#fa8585] p-3 text-sm font-bold text-white transition-all active:scale-[0.98]"
              >
                <Download size={18} />
                <span>Install App</span>
              </button>
            ) : platform === 'android' ? (
              <AndroidManualSteps />
            ) : (
              <IOSSteps />
            )}

            {/* Don't show again + Skip */}
            <div className="mt-5 flex flex-col items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-slate-300 text-[#fa8585] accent-[#fa8585]"
                />
                <span className="text-[11px] text-slate-400">Don't show this again</span>
              </label>
              <button
                onClick={handleDismiss}
                className="text-xs font-semibold text-slate-400 transition-colors hover:text-slate-600"
              >
                Skip for now
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function BenefitChip({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-2.5 py-2">
      <span className="text-[#fa8585]">{icon}</span>
      <span className="text-[12px] font-medium text-slate-600">{text}</span>
    </div>
  );
}

function IOSSteps() {
  return (
    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-3.5">
      <p className="mb-2.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
        How to install
      </p>

      <div className="space-y-2.5">
        <StepRow
          step={1}
          description={
            <>
              Tap <MoreHorizIcon /> at the bottom right of Safari
            </>
          }
        />
        <StepRow
          step={2}
          description={
            <>
              Tap <ShareIcon /> <strong>Share</strong>
            </>
          }
        />
        <StepRow
          step={3}
          description={
            <>
              Tap <ViewMoreIcon /> <strong>View More</strong>
            </>
          }
        />
        <StepRow
          step={4}
          description={
            <>
              Tap <strong>Add to Home Screen</strong>
            </>
          }
        />
        <StepRow
          step={5}
          description={
            <>
              Tap <strong>Add</strong> (top right)
            </>
          }
        />

        <p className="pt-2 text-[11px] text-amber-600">
          <strong>Note:</strong> Safari only — Chrome/Firefox can't install web apps on iPhone.
        </p>
      </div>
    </div>
  );
}

function AndroidManualSteps() {
  return (
    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-3.5">
      <p className="mb-2.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
        How to install
      </p>

      <div className="space-y-2.5">
        <StepRow
          step={1}
          description={
            <>
              Tap <MoreVertIcon /> menu (top right)
            </>
          }
        />
        <StepRow
          step={2}
          description={
            <>
              Tap <strong>"Add to Home screen"</strong> or <strong>"Install app"</strong>
            </>
          }
        />
        <StepRow
          step={3}
          description={
            <>
              Tap <strong>Install</strong> to confirm
            </>
          }
        />
      </div>
    </div>
  );
}

function StepRow({ step, description }: { step: number; description: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-[20px] w-[20px] flex-shrink-0 items-center justify-center rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] border border-slate-100 text-slate-700 text-[10px] font-bold">
        {step}
      </div>
      <div className="flex-1 text-[11.5px] text-slate-700 flex items-center gap-1.5 leading-snug tracking-tight">
        {description}
      </div>
    </div>
  );
}

// iOS Share icon (box with arrow up)
function ShareIcon() {
  return (
    <span className="inline-flex h-[20px] w-[20px] items-center justify-center rounded-[5px] bg-white border border-slate-200 shadow-sm text-blue-500">
      <Share size={10} strokeWidth={2.5} />
    </span>
  );
}

function MoreHorizIcon() {
  return (
    <span className="inline-flex h-[20px] w-[20px] items-center justify-center rounded-[5px] bg-white border border-slate-200 shadow-sm">
      <svg viewBox="0 0 24 24" className="h-[14px] w-[14px] text-blue-500" fill="currentColor">
        <circle cx="5" cy="12" r="2" />
        <circle cx="12" cy="12" r="2" />
        <circle cx="19" cy="12" r="2" />
      </svg>
    </span>
  );
}

function ViewMoreIcon() {
  return (
    <span className="inline-flex h-[20px] w-[20px] items-center justify-center rounded-[5px] bg-white border border-slate-200 shadow-sm">
      <svg
        viewBox="0 0 24 24"
        className="h-3 w-3 text-slate-700"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </span>
  );
}

function MoreVertIcon() {
  return (
    <span className="inline-flex h-[20px] w-[20px] items-center justify-center rounded-[5px] bg-white border border-slate-200 shadow-sm">
      <svg viewBox="0 0 24 24" className="h-[14px] w-[14px] text-slate-700" fill="currentColor">
        <circle cx="12" cy="5" r="2" />
        <circle cx="12" cy="12" r="2" />
        <circle cx="12" cy="19" r="2" />
      </svg>
    </span>
  );
}
